<script lang="ts">
	import Render from '#components/render.svelte'
	import type { StoryblokRichtext } from '#library/components.schema.build.ts'
	import type { Heading } from 'storyloco/heading'

	interface Props {
		title?: Heading | string
		subtitle?: Heading
		body?: StoryblokRichtext
		size?: string
		plain?: boolean
		split?: boolean
		flip?: boolean
		pad?: boolean
	}

	let { title, subtitle, body, size, plain, split, flip, pad }: Props = $props()
</script>

{#snippet heading()}
	{#if title?.text || typeof title === 'string'}
		<svelte:element
			this={title?.tag || 'div'}
			class="title"
			class:medium={size === 'medium'}
			class:small={size === 'small'}
			class:plain
		>
			{title?.text || title}
		</svelte:element>
	{/if}
{/snippet}

{#if split}
	{@render heading()}
{/if}
<div class="copy" class:flip class:pad>
	{#if !split}
		{@render heading()}
	{/if}
	{#if subtitle?.text || (typeof subtitle === 'string' && subtitle.length)}
		<svelte:element
			this={subtitle?.tag || 'div'}
			class="subtitle"
			class:medium={size === 'medium'}
			class:small={size === 'small'}
		>
			{subtitle?.text || subtitle}
		</svelte:element>
	{/if}
	{#if body}
		<div class="body" class:split>
			<Render content={body} />
		</div>
	{/if}
</div>

<style lang="css">
	.copy {
		container-type: inline-size;
		max-width: 100%;

		&.pad {
			padding-block-end: 4rem;
		}

		@media (--from-tablet) {
			&.flip {
				grid-column: 2;
			}
		}
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
		margin: 0;
		max-width: 13ch; /* Keep an eye on this */
		text-wrap: balance;

		&.medium {
			font: 600 var(--f-h3);
			letter-spacing: var(--ls-h3);
			max-width: 20ch;
		}

		&.small {
			font: 600 var(--f-h4);
			letter-spacing: var(--ls-h4);
			max-width: 25ch; /* Keep an eye on this */
		}

		&.plain {
			color: var(--c-midground);
		}
	}

	.subtitle {
		color: var(--c-midground);
		font: 500 var(--f-h4);
		letter-spacing: var(--ls-h4);
		margin-block: 0.875rem 0;
		text-wrap: balance;

		@media (--from-mobile) {
			margin-block-end: 1rem;
		}

		&.medium {
			/* margin-block-start: 0; */
		}

		&.small {
			font: 500 var(--f-h5);
			letter-spacing: var(--ls-h5);
			margin-block-start: 0;
		}
	}

	.body {
		color: var(--c-midground);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-block-start: 0.5rem;

		@media (--from-mobile) {
			margin-block-start: 1.25rem;

			&.split {
				margin-block-start: 0;
			}

			@container (width > 46em) {
				columns: 2;

				:global {
					p:only-child,
					p:only-of-type {
						margin: 0 !important;
					}
				}
			}
		}

		:global {
			p a {
				color: var(--c-foreground);
				font-weight: 500;
				text-decoration: underline;
			}

			p:first-child,
			p:first-of-type {
				margin-block-start: 0;
			}

			p:last-child,
			p:last-of-type {
				margin-block-end: 0;
			}

			p:only-child,
			p:only-of-type {
				margin-block: 0.5rem 0.875rem;

				@media (--from-mobile) {
					margin-block: 1rem 0.875rem;
				}
			}

			&.split p:only-of-type {
				margin-block-start: 0.5rem;
			}

			p + .link {
				@media (--from-mobile) {
					margin-block-start: 0.75rem;
				}
			}

			.link {
				@media (--from-mobile) {
					margin-block-start: 0.0625rem;
				}
			}

			.button {
				margin-block-start: 1.5rem;

				@media (--from-mobile) {
					margin-block-start: 2.5rem;
				}
			}

			.item {
				padding-block: 1.5rem;

				.title:not(.medium):not(.small):not(.large) {
					font: 600 var(--f-h5);
					letter-spacing: var(--ls-h5);
				}

				.arrow {
					size: 2rem;
				}
			}
		}
	}
</style>
